import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { useCurrency } from "../context/CurrencyContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Wallet, TrendingUp, TrendingDown, CalendarDays, Sparkles, CreditCard, MapPin, Building2, ArrowUpRight, ArrowDownRight } from "lucide-react";

const TYPE_IMAGES = {
  chalet: "https://images.unsplash.com/photo-1691520486119-3cff18780ae1",
  apartment: "https://images.pexels.com/photos/31817157/pexels-photo-31817157.jpeg",
  house: "https://images.unsplash.com/photo-1623680904963-5580d963e18e",
  sports_field: "https://images.unsplash.com/photo-1505305976870-c0be1cd39939",
  tent: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4",
  inn: "https://images.pexels.com/photos/24805054/pexels-photo-24805054.jpeg",
  caravan: "https://images.unsplash.com/photo-1523987355523-c7b5b0dd90a7",
  other: "https://images.unsplash.com/photo-1623680904963-5580d963e18e",
};

export default function Dashboard() {
  const { t, lang } = useLang();
  const { format: money, code: ccy } = useCurrency();
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [comp, setComp] = useState(null);
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [propsRes, locRes] = await Promise.all([
          api.get("/properties"),
          api.get("/oman/locations"),
        ]);
        const props = propsRes.data;
        setProperties(props);
        setLocations(locRes.data);
        const [s, c] = await Promise.all([
          api.get("/reports/summary?period=month"),
          api.get("/reports/comparison"),
        ]);
        setSummary(s.data);
        setComp(c.data);
        const today = new Date().toISOString().slice(0, 10);
        const end = new Date();
        end.setDate(end.getDate() + 30);
        const endStr = end.toISOString().slice(0, 10);
        if (props[0]) {
          const b = await api.get("/bookings", { params: { property_id: props[0].id, start: today, end: endStr } });
          setUpcoming(b.data.slice(0, 5));
        }
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  const daysLeft = useMemo(() => {
    if (!user?.trial_end) return 0;
    const end = new Date(user.trial_end);
    return Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
  }, [user]);

  const handlePay = async () => {
    setPaying(true);
    try {
      await api.post("/subscription/mock-pay");
      await refresh();
      toast.success(t("paid_success"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPaying(false);
    }
  };

  const mainProp = properties[0];
  const heroImg = mainProp ? TYPE_IMAGES[mainProp.type] || TYPE_IMAGES.other : TYPE_IMAGES.other;
  const govAr = mainProp && locations[mainProp.governorate]?.ar;
  const wilayatIdx = mainProp && locations[mainProp.governorate]?.wilayats?.indexOf(mainProp.wilayat);
  const wilayatAr = mainProp && wilayatIdx > -1 ? locations[mainProp.governorate]?.wilayats_ar?.[wilayatIdx] : null;
  const govLabel = lang === "ar" ? (govAr || mainProp?.governorate) : mainProp?.governorate;
  const wilayatLabel = lang === "ar" ? (wilayatAr || mainProp?.wilayat) : mainProp?.wilayat;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero card */}
      <div className="relative rounded-3xl overflow-hidden border border-[#E5E1D8] shadow-sm">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${heroImg}')` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1E17]/85 via-[#1C1E17]/60 to-transparent" />
        <div className="relative p-6 sm:p-8 text-white flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider opacity-80">{t("welcome")}</div>
            <h1 className="text-3xl sm:text-4xl font-semibold mt-1">
              {user?.name}
            </h1>
            {mainProp && (
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/15 border border-white/25 backdrop-blur-sm">
                  <Building2 size={13} /> {mainProp.name}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-[#2A5948] border border-[#2A5948]" data-testid="dashboard-property-type-badge">
                  {t(`type_${mainProp.type}`)}
                </span>
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 border border-white/20">
                  <MapPin size={13} /> {govLabel} — {wilayatLabel}
                </span>
              </div>
            )}
          </div>

          <TrialCard days={daysLeft} paid={user?.is_paid} onPay={handlePay} paying={paying} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-income" icon={TrendingUp} label={t("monthly_income")} value={fmt(summary?.income, ccy)} tone="green" />
        <StatCard testid="stat-expense" icon={TrendingDown} label={t("monthly_expense")} value={fmt(summary?.expense, ccy)} tone="red" />
        <StatCard testid="stat-profit" icon={Wallet} label={t("monthly_profit")} value={fmt(summary?.profit, ccy)} tone="brand" />
        <StatCard testid="stat-days" icon={CalendarDays} label={t("rented_days")} value={summary?.rented_days ?? 0} tone="accent" />
      </div>

      {/* Comparison + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t("comparison")}</h3>
            <Link to="/app/reports" data-testid="dashboard-view-reports" className="text-xs text-[#2A5948] hover:underline">→</Link>
          </div>
          {comp ? (
            <div className="mt-3 grid grid-cols-2 gap-4">
              <ComparisonRow testid="dash-comp-week" label={t("this_week")} sub={t("last_week")} cur={comp.week.current} prev={comp.week.previous} />
              <ComparisonRow testid="dash-comp-month" label={t("this_month")} sub={t("last_month")} cur={comp.month.current} prev={comp.month.previous} />
            </div>
          ) : (
            <div className="mt-3 text-sm text-[#5C6053]">…</div>
          )}
        </div>

        <div className="bg-white border border-[#E5E1D8] rounded-2xl p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t("upcoming")}</h3>
            <Link to="/app/calendar" data-testid="dashboard-open-calendar" className="text-xs text-[#2A5948] hover:underline">→</Link>
          </div>
          <div className="mt-3 space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-[#5C6053]">—</div>}
            {upcoming.map((b) => (
              <div key={b.id} data-testid={`upcoming-${b.id}`} className="flex items-center justify-between border border-[#E5E1D8] rounded-lg px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{b.guest_name || t("status_" + b.status)}</div>
                  <div className="text-xs text-[#5C6053]">{b.date}</div>
                </div>
                <div className="flex flex-col items-end">
                  <span className={`text-xs px-2 py-0.5 rounded ${b.status === "rented" ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FFF3D6] text-[#8A6D1F]"}`}>
                    {t("status_" + b.status)}
                  </span>
                  <span className="text-xs mt-1">{money(b.amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="bg-white border border-[#E5E1D8] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 text-[#2A5948] font-medium">
          <Sparkles size={16} />
          {t("quick_add")}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/app/calendar" data-testid="quick-open-calendar" className="brand-btn px-4 py-2 rounded-lg text-sm">
            {t("calendar")}
          </Link>
          <Link to="/app/finances" data-testid="quick-open-finances" className="px-4 py-2 rounded-lg text-sm border border-[#E5E1D8] hover:bg-[#F3EFE7]">
            {t("finances")}
          </Link>
          <Link to="/app/reports" data-testid="quick-open-reports" className="px-4 py-2 rounded-lg text-sm border border-[#E5E1D8] hover:bg-[#F3EFE7]">
            {t("reports")}
          </Link>
        </div>
      </div>
    </div>
  );
}

function fmt(n, ccy = "OMR") {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n) + " " + ccy;
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const toneMap = {
    green: "bg-[#E6F1E7] text-[#2A5948]",
    red: "bg-[#FBE9E4] text-[#A85540]",
    brand: "bg-[#2A5948] text-white",
    accent: "bg-[#FFF3D6] text-[#8A6D1F]",
  };
  return (
    <div data-testid={testid} className="bg-white border border-[#E5E1D8] rounded-2xl p-4 sm:p-5 shadow-sm hover:-translate-y-0.5 transition-transform">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
        <Icon size={18} />
      </div>
      <div className="mt-3 text-xs text-[#5C6053]">{label}</div>
      <div className="mt-1 text-xl sm:text-2xl font-semibold">{value}</div>
    </div>
  );
}

function ComparisonRow({ label, sub, cur, prev, testid }) {
  const { t } = useLang();
  const { format: money } = useCurrency();
  const diff = (cur.profit || 0) - (prev.profit || 0);
  const pct = prev.profit ? ((diff / Math.abs(prev.profit)) * 100).toFixed(1) : (cur.profit ? "100.0" : "0.0");
  const up = diff >= 0;
  return (
    <div data-testid={testid} className="rounded-xl border border-[#E5E1D8] p-3">
      <div className="text-xs text-[#5C6053]">{label} vs {sub}</div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <div className="text-lg font-semibold">{money(cur.profit || 0)}</div>
        <div className={`text-xs inline-flex items-center gap-0.5 px-2 py-0.5 rounded ${up ? "bg-[#E6F1E7] text-[#2A5948]" : "bg-[#FBE9E4] text-[#A85540]"}`}>
          {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
          {pct}%
        </div>
      </div>
      <div className="text-[11px] text-[#5C6053] mt-1">
        {t("income")} {(cur.income || 0).toFixed(2)} · {t("expense")} {(cur.expense || 0).toFixed(2)}
      </div>
    </div>
  );
}

function TrialCard({ days, paid, onPay, paying }) {
  const { t } = useLang();
  if (paid) {
    return (
      <div className="rounded-2xl bg-white/15 border border-white/30 backdrop-blur-sm px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
          <CreditCard size={16} />
        </div>
        <div className="text-sm">
          <div className="font-medium">{t("paid_success")}</div>
        </div>
      </div>
    );
  }
  // Trial-active user: subtle information card, no urgent CTA
  if (days > 0) {
    return (
      <div
        data-testid="trial-card"
        className="rounded-2xl bg-white/10 border border-white/20 backdrop-blur-sm px-4 py-3 flex items-center gap-3"
      >
        <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div className="text-sm">
          <div className="font-medium">{t("trial_active")}</div>
          <div className="opacity-80 text-xs">{`${days} ${t("trial_days_left")}`}</div>
        </div>
      </div>
    );
  }
  // Trial expired → still no CTA here; PaywallGate handles the actual subscribe flow
  return (
    <Link to="/pay" data-testid="trial-card" className="rounded-2xl bg-white/15 border border-white/30 backdrop-blur-sm px-4 py-3 flex items-center gap-3 hover:bg-white/25 transition-colors">
      <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
        <Sparkles size={16} />
      </div>
      <div className="text-sm">
        <div className="font-medium">{t("trial_expired")}</div>
      </div>
      <span
        data-testid="subscribe-btn"
        className="ms-2 px-3 py-1.5 text-xs rounded-lg bg-[#C96A52] hover:bg-[#A85540]"
      >
        {t("subscribe_now")}
      </span>
    </Link>
  );
}
